import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car } from 'lucide-react';
import CTA from '../../components/CTA';

const DerryHypnotherapy: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Hypnotherapy Derry/Londonderry | Online & In-Person Sessions</title>
        <meta
          name="description"
          content="Professional hypnotherapy serving Derry/Londonderry. Online sessions available or in-person at our Belfast clinic. Anxiety, stress, chronic pain, trauma recovery. CNHC registered."
        />
        <meta name="keywords" content="hypnotherapy Derry, hypnotherapist Londonderry, anxiety treatment Derry, therapist Derry, hypnotherapy Londonderry, trauma therapy Derry, stress management Derry" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/derry-hypnotherapy/" />
        <meta property="og:title" content="Hypnotherapy Derry/Londonderry | Online & In-Person Sessions" />
        <meta property="og:description" content="Professional hypnotherapy serving Derry/Londonderry. Online sessions or in-person at Belfast clinic. Anxiety, stress, chronic pain, trauma recovery." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Serving Derry/Londonderry",
            "description": "CNHC registered hypnotherapy serving Derry/Londonderry. Online sessions or in-person at our Belfast clinic. Specializing in anxiety, stress, chronic pain, trauma recovery, and medical procedure anxiety.",
            "url": "https://www.sharonclare-hypnotherapy.com/derry-hypnotherapy/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "areaServed": {
              "@type": "City",
              "name": "Derry",
              "alternateName": "Londonderry",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": [
              {
                "@type": "Place",
                "name": "The Arzen Collective",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "The Arzen Collective, 13B Antrim Street",
                  "addressLocality": "Lisburn",
                  "addressRegion": "County Antrim",
                  "postalCode": "BT28",
                  "addressCountry": "GB"
                }
              },
              {
                "@type": "Place",
                "name": "Belfast Clinic",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                  "addressLocality": "Belfast",
                  "addressRegion": "County Down",
                  "postalCode": "BT7 2FZ",
                  "addressCountry": "GB"
                }
              }
            ],
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ],
            "medicalSpecialty": "Clinical Hypnotherapy",
            "serviceType": [
              "Anxiety Treatment",
              "Stress Management",
              "Chronic Pain Management",
              "Trauma Recovery",
              "Medical Procedure Anxiety",
              "Radiotherapy Anxiety Support"
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Professional Hypnotherapy Serving Derry/Londonderry
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Online sessions for Derry residents, or in-person at our Belfast clinic on the Ormeau Road
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3">
                <MapPin className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">Online sessions</span> available for Derry residents • <span className="font-semibold">In-person</span> at the Belfast or Lisburn clinic
                </p>
              </div>
              <p className="text-gray-600 mt-2">Secure video sessions from the comfort of your home</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Hypnotherapy for Derry and Londonderry
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                I'm proud to serve the Derry/Londonderry community through convenient online sessions and two in-person clinic locations. For Derry residents, <strong>online sessions</strong> are usually the most practical option — they provide the same professional, evidence-based treatment from the comfort of your own home, with no travel at all. If you would rather meet face to face, I offer in-person appointments at my Belfast clinic (Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ) and at my Lisburn clinic (The Arzen Collective, 13B Antrim Street, Lisburn BT28), where days and times vary and early evening appointments are available.
              </p>
              <p>
                I understand that Derry faces unique challenges. As a border city with a complex history, many residents experience ongoing effects of post-conflict trauma, economic pressures, and the frustration of long NHS waiting times for mental health support. Solution-focused hypnotherapy offers an evidence-based, complementary approach that can work alongside NHS care or provide support when waiting lists make timely help difficult to access.
              </p>
              <p>
                My specializations are particularly relevant to the Derry community. Whether you're dealing with anxiety, stress, chronic pain, trauma recovery, or medical procedure anxiety, the neuroscience-based approach I use has helped many clients from across Northern Ireland find relief and regain control of their lives.
              </p>
              <p>
                Online sessions via secure video call are the most popular choice for Derry clients. They provide the same professional, evidence-based treatment with the convenience of attending from your own home. This flexibility works perfectly for those who juggle work commitments, childcare, or simply prefer the comfort of their own space.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Sharon for Derry */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Why Choose Sharon Clare Hypnotherapy for Derry?
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  CNHC Registered & Professionally Qualified
                </h3>
                <p className="text-gray-700">
                  As a CNHC registered clinical hypnotherapist with 15 years of nursing experience, I bring both professional expertise and genuine healthcare understanding to every session. You can trust that you're receiving evidence-based treatment from a qualified professional who adheres to the highest standards of practice and ethical guidelines.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Understanding Post-Conflict Trauma Context
                </h3>
                <p className="text-gray-700">
                  Growing up and working in Northern Ireland, I understand the unique context of trauma and stress in communities affected by the Troubles. While I don't claim to be a trauma specialist exclusively, my approach is sensitive to the complex historical and community factors that can contribute to anxiety, stress, and emotional difficulties in Derry and across Northern Ireland.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Complementary to NHS Care
                </h3>
                <p className="text-gray-700">
                  NHS waiting times for mental health services in the Western Trust area can be frustratingly long - often 6-12 months or more for talking therapies. Hypnotherapy provides an evidence-based alternative that can offer relief while you wait, or work alongside NHS treatment. Many Derry clients have found that hypnotherapy fills the gap when they need support but can't access timely NHS care.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Flexible Online Scheduling
                </h3>
                <p className="text-gray-700">
                  Online sessions offer maximum flexibility for Derry residents. Choose appointment times that suit your schedule, with no travel required. This is the most popular option for clients in the Derry/Londonderry area. In-person sessions are also available at our Belfast clinic on the Ormeau Road.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Online Sessions Available
                </h3>
                <p className="text-gray-700">
                  Secure online sessions via video call provide professional hypnotherapy treatment from the comfort of your own home in Derry. This is the most popular option for Derry clients, ideal for those with busy schedules, mobility concerns, or caring responsibilities. The therapeutic effectiveness is equivalent to in-person sessions. In-person appointments are also available at our Belfast clinic on the Ormeau Road.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Evidence-Based Neuroscience Approach
                </h3>
                <p className="text-gray-700">
                  Solution-focused hypnotherapy combines the latest neuroscience research with proven therapeutic techniques. Rather than dwelling on problems or the past, we focus on solutions, building resilience, and practical strategies for change. This forward-focused approach resonates with many Derry clients who want to move beyond historical trauma and create a better future for themselves and their families.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services for Derry Clients */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Hypnotherapy Services for Derry Clients
            </h2>
            <div className="space-y-8">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4">
                  Anxiety and Stress Management
                </h3>
                <p className="text-gray-700 mb-4">
                  Anxiety is one of the most common reasons Derry clients seek hypnotherapy support. Whether you're experiencing generalized anxiety, panic attacks, social anxiety, or stress related to work, finances, or family pressures, solution-focused hypnotherapy can help you regain calm and control. The economic challenges facing many Derry communities, combined with the legacy of conflict-related stress, create unique pressures that can manifest as anxiety disorders.
                </p>
                <p className="text-gray-700 mb-4">
                  Rather than focusing on the causes of your anxiety, our evidence-based approach works with your brain's natural stress response systems. By teaching you practical relaxation techniques, building resilience, and helping you develop better sleep patterns, hypnotherapy addresses anxiety at its neurological root. Most clients notice significant improvements within 4-6 sessions, experiencing reduced worry, better sleep, improved confidence, and a greater sense of control over their emotions.
                </p>
                <p className="text-gray-700">
                  For Derry residents, addressing anxiety isn't just about symptom relief - it's about breaking free from patterns that may have been passed down through generations, creating a healthier future for yourself and your family. Online sessions let you do that work from a familiar, private space of your own, so the relaxation process can begin before the session even starts.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4">
                  Chronic Pain Management
                </h3>
                <p className="text-gray-700 mb-4">
                  Chronic pain conditions like fibromyalgia, back pain, arthritis, and nerve pain are surprisingly common in Northern Ireland, and Derry is no exception. Living with persistent pain is exhausting - it affects your sleep, mood, relationships, work, and quality of life. Traditional pain management often relies heavily on medication, but neuroscience shows us that pain is processed in the brain, and when we're stressed or anxious, pain signals intensify.
                </p>
                <p className="text-gray-700 mb-4">
                  Solution-focused hypnotherapy for chronic pain works by calming your nervous system, reducing pain perception, and teaching your brain healthier patterns of pain processing. By addressing the stress and anxiety that often accompany chronic pain, many clients experience significant reductions in their pain levels and improvements in their ability to function. While hypnotherapy doesn't "cure" chronic conditions, it can be a powerful tool in your pain management toolkit, working alongside medical treatment.
                </p>
                <p className="text-gray-700">
                  For Derry clients dealing with chronic pain, hypnotherapy offers particular benefits. The evidence-based approach, combined with practical relaxation techniques, supports a comprehensive approach to pain management. Online sessions are especially convenient for chronic pain sufferers, as you can attend from a comfortable position in your own home without the physical strain of a long journey.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4">
                  Weight Management and Emotional Eating
                </h3>
                <p className="text-gray-700 mb-4">
                  Weight management isn't about willpower - it's about addressing the emotional and psychological patterns that drive eating behaviors. Many people in Derry and across Northern Ireland struggle with emotional eating, comfort eating during stressful times, yo-yo dieting, and the frustration of losing and regaining the same weight repeatedly. If you've tried every diet and nothing seems to work long-term, hypnotherapy addresses the underlying mindset that keeps you stuck.
                </p>
                <p className="text-gray-700 mb-4">
                  Solution-focused hypnotherapy for weight management helps you develop a healthier relationship with food, recognize emotional eating triggers, build genuine motivation for change, and create lasting behavioral shifts. Rather than restrictive dieting, we focus on understanding why you eat when you're not hungry, breaking habits that no longer serve you, and building confidence in your ability to make different choices. The approach is compassionate, non-judgmental, and focused on sustainable change.
                </p>
                <p className="text-gray-700">
                  For Derry clients, addressing weight concerns often connects to broader issues of stress management and self-care. Economic pressures, family responsibilities, and limited time for self-care can all contribute to comfort eating patterns. By addressing these underlying stressors while also working on your relationship with food, hypnotherapy offers a holistic path to healthier weight management that feels achievable and sustainable.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4">
                  Quit Smoking and Vaping
                </h3>
                <p className="text-gray-700 mb-4">
                  Smoking rates in Derry and the wider Western Trust area remain higher than the Northern Ireland average, despite growing awareness of health risks. Whether you've smoked for 5 years or 50, quitting feels impossibly difficult without the right support. The physical addiction to nicotine is actually relatively short-lived - it's the psychological habit, the emotional associations, and the belief that you "need" cigarettes that keep people smoking. This is where hypnotherapy excels.
                </p>
                <p className="text-gray-700 mb-4">
                  My stop smoking programme uses solution-focused hypnotherapy to break the psychological addiction to cigarettes or vaping. We work on changing your mindset from "giving up" something you enjoy to freeing yourself from something that controls you. By addressing the stress, anxiety, and emotional triggers that drive smoking, and building your confidence in your ability to be a non-smoker, many clients quit successfully after just a few sessions. Success rates are highest for motivated clients who genuinely want to quit.
                </p>
                <p className="text-gray-700">
                  For Derry residents, the financial benefits of quitting smoking are substantial - a 20-a-day smoker saves thousands of pounds a year. Combined with the health benefits and the pride of breaking free from addiction, quitting smoking can be truly life-changing. The journey from Derry to the clinic for your sessions becomes a symbolic journey away from smoking and toward your healthier future.
                </p>
              </div>
            </div>

            <div className="text-center mt-12">
              <a href="/services" className="btn-primary text-lg px-8 py-4 inline-flex items-center">
                View All Services
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* How Sessions Work for Derry Clients */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How Sessions Work for Derry Clients
            </h2>
            <div className="bg-white rounded-2xl p-8 mb-8">
              <div className="flex items-start space-x-4 mb-6">
                <MapPin className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">Online Sessions (Most Popular for Derry)</h3>
                  <p className="text-gray-700 mb-2">
                    Secure video sessions from the comfort of your home. Same professional, evidence-based treatment with no travel required. Flexible scheduling including evenings by arrangement.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4 mb-6">
                <Car className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">In-Person at the Belfast Clinic</h3>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Address:</span> Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Hours:</span> Clinic days and times vary, and early evening appointments are available
                  </p>
                  <p className="text-gray-700 mt-4 font-semibold">Also available: Lisburn Clinic</p>
                  <p className="text-gray-700 mb-1">
                    The Arzen Collective, 13B Antrim Street, Lisburn BT28 — clinic days and times vary here too. Current Belfast availability and online booking are on the{' '}
                    <a
                      href="https://belfastchiropracticclinic.janeapp.co.uk/#/staff_member/37"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700 font-medium underline"
                    >
                      clinic booking page
                    </a>
                    .
                  </p>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Online Sessions Are Equally Effective</h3>
                <p className="text-gray-700">
                  Research shows that online hypnotherapy sessions are just as effective as in-person treatment. Many Derry clients prefer the convenience and flexibility of online sessions, which eliminate travel time entirely while providing the same professional, evidence-based support.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Derry Area Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "The drive from Derry was worth it. Sharon understood my anxiety around the Troubles without me having to explain everything. After four sessions, I'm sleeping better and the constant worry has lifted. I feel like I can finally move forward instead of being stuck in the past."
                </p>
                <p className="font-semibold text-gray-900">M.D., Derry City - Trauma and Anxiety</p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I couldn't face the NHS waiting list - 18 months for counselling when I was struggling every single day. The online sessions with Sharon were a lifeline. I could attend from home in Waterside, and within six weeks my anxiety had reduced so much I could actually function again."
                </p>
                <p className="font-semibold text-gray-900">P.K., Waterside, Derry - Severe Anxiety</p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Living with chronic pain for 8 years, I'd tried everything. The online sessions meant I could finally prioritize my health without missing work or traveling. Sharon's approach actually helped - my pain levels have reduced significantly and I'm not relying on painkillers as much."
                </p>
                <p className="font-semibold text-gray-900">T.B., Strabane - Chronic Pain Management</p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "After smoking for 25 years, I never thought I'd quit. Three sessions with Sharon and I haven't had a cigarette in 6 months. The money I'm saving alone makes the drive from Derry worthwhile, but honestly it's the freedom from addiction that's life-changing."
                </p>
                <p className="font-semibold text-gray-900">J.M., Limavady - Quit Smoking</p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "The economic stress in Derry is real, and I was comfort eating constantly, gaining more and more weight. Sharon helped me understand why I was eating emotionally and gave me tools to break those patterns. I've lost 3 stone and kept it off for over a year now."
                </p>
                <p className="font-semibold text-gray-900">A.C., Derry City - Weight Management</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Derry Clients
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How far are your clinics from Derry city centre?
                </h3>
                <p className="text-gray-700">
                  Both are a fair distance from Derry. The Belfast clinic at 228 Ormeau Road is roughly 70 miles via the A6 and M2, then south through the city onto the Ormeau Road (A24). The Lisburn clinic on Antrim Street is a little shorter, as you leave the M1 before Belfast itself. That distance is exactly why most Derry clients work with me online instead — same treatment, no journey.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Do you offer online sessions for Derry clients?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. I offer secure online hypnotherapy sessions via video call for Derry residents who can't make the journey to either clinic or who simply prefer the convenience of online therapy. Online sessions provide exactly the same professional, evidence-based treatment as in-person sessions. Many clients choose online sessions due to work schedules, childcare responsibilities, mobility concerns, or because they feel more relaxed in their own home environment. The therapeutic effectiveness is equivalent to face-to-face sessions.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  What days are you available?
                </h3>
                <p className="text-gray-700">
                  Clinic days and times at the Belfast clinic vary, and early evening appointments are available - current availability and online booking are on the{' '}
                  <a
                    href="https://belfastchiropracticclinic.janeapp.co.uk/#/staff_member/37"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700 font-medium underline"
                  >
                    clinic booking page
                  </a>
                  . That said, Derry is roughly 70 miles from the Belfast clinic, so online sessions are strongly recommended as the most practical option for Derry residents. Online sessions offer more flexible scheduling throughout the week, including evenings, making it much easier to fit therapy around work and family commitments without a long journey.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with trauma related to the Troubles?
                </h3>
                <p className="text-gray-700">
                  Solution-focused hypnotherapy can be very effective for trauma and PTSD symptoms. While I don't specialize exclusively in conflict-related trauma, I have a deep understanding of the Northern Ireland context and the ongoing impact of the Troubles on individuals and communities. The solution-focused approach works by helping you build resilience, reduce anxiety, improve sleep, and move forward rather than dwelling on traumatic past events. Many clients from Derry and across Northern Ireland have found this forward-focused approach particularly helpful for breaking free from historical trauma patterns.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How does hypnotherapy compare to NHS counselling?
                </h3>
                <p className="text-gray-700">
                  Hypnotherapy is complementary to NHS care, not a replacement. The key difference is availability - NHS waiting times for psychological therapies in the Western Trust area can be 6-12 months or longer, while you can usually access hypnotherapy within 1-2 weeks. The approach is also different: solution-focused hypnotherapy concentrates on building solutions and resilience rather than analyzing problems in detail. Many Derry clients use hypnotherapy while waiting for NHS services, or choose it as an alternative when they need timely support for anxiety, stress, or pain management.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is the journey from Derry worth it?
                </h3>
                <p className="text-gray-700">
                  While I can't make this decision for you, I can share that many Derry clients feel the journey is absolutely worthwhile. The specialist nature of the treatment, weekend availability, peaceful rural setting, and the fact that you're investing dedicated time in your mental health all contribute to the therapeutic value. Several clients have commented that the drive itself becomes part of their self-care routine - time away from daily pressures to focus on themselves. If traveling feels difficult, online sessions offer the same professional treatment without the journey.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Do you work with clients from the Republic of Ireland?
                </h3>
                <p className="text-gray-700">
                  Yes, as a border city, Derry has strong connections with County Donegal and the Republic of Ireland. I welcome clients from across the border who are seeking hypnotherapy support. Online sessions are particularly convenient for Republic of Ireland clients, eliminating any cross-border travel considerations while still providing access to professional hypnotherapy services.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  What conditions do you treat most in Derry clients?
                </h3>
                <p className="text-gray-700">
                  The most common issues I see from Derry clients are anxiety disorders (including generalized anxiety, panic attacks, and trauma-related anxiety), chronic stress, chronic pain conditions (particularly fibromyalgia and back pain), smoking cessation, and weight management. The economic pressures and post-conflict legacy in Derry contribute to higher rates of anxiety and stress-related conditions. Solution-focused hypnotherapy is highly effective for all of these issues, with most clients experiencing significant improvements within 4-6 sessions.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How many sessions do I need?
                </h3>
                <p className="text-gray-700">
                  The number of sessions varies depending on your individual situation and goals. Most clients see significant improvements within 4-6 sessions for conditions like anxiety, stress, or smoking cessation. Chronic pain management or complex trauma may benefit from a longer programme of 6-10 sessions. We'll discuss your specific needs during the initial consultation and create a treatment plan tailored to your circumstances. I also offer a discounted 4-session package which provides better value for clients traveling from Derry.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can I claim hypnotherapy on health insurance?
                </h3>
                <p className="text-gray-700">
                  Some private health insurance policies cover hypnotherapy when provided by a CNHC registered hypnotherapist for recognized conditions such as anxiety, pain management, or smoking cessation. Coverage varies significantly between providers and individual policies. I recommend contacting your insurance provider directly to ask about coverage for CNHC registered hypnotherapy. I can provide receipts and documentation for insurance claims as needed. Even without insurance coverage, many Derry clients find the investment worthwhile given the significant improvement in their quality of life.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />
    </main>
  );
};

export default DerryHypnotherapy;
